<template>
    <div class="activity-list-page">
        <div class="container content">
            <div class="header-card">
                <div class="header-main">
                    <div class="header-badge">专题训练与真题精讲</div>
                    <h1 class="header-title">活动中心</h1>
                    <p class="header-subtitle">按专题快速进入训练路径，边学边练边打卡</p>
                </div>
                <div class="header-stats" v-if="!loading && activities.length">
                    <div class="stat-item">
                        <div class="stat-value">{{ total }}</div>
                        <div class="stat-label">活动总数</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-value">{{ freeActivityCount }}</div>
                        <div class="stat-label">免费活动</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-value">{{ totalEnrollCount }}</div>
                        <div class="stat-label">累计参与</div>
                    </div>
                </div>
            </div>

            <div class="toolbar-card">
                <div class="toolbar">
                <el-input v-model.trim="keyword" class="search-input" placeholder="搜索活动标题关键词" clearable>
                    <template #prefix>
                        <el-icon>
                            <Search />
                        </el-icon>
                    </template>
                </el-input>

                <el-select v-model="selectedTag" clearable placeholder="按标签筛选" class="tag-select">
                    <el-option v-for="tag in allTags" :key="tag" :label="tag" :value="tag" />
                </el-select>

                <el-select v-model="sortType" class="sort-select">
                    <el-option label="最新发布" value="latest" />
                    <el-option label="参与人数最多" value="popular" />
                    <el-option label="价格从低到高" value="priceAsc" />
                </el-select>
                </div>
            </div>

            <div v-if="errorMessage" class="error-state">
                <el-result icon="error" title="活动加载失败" :sub-title="errorMessage">
                    <template #extra>
                        <el-button type="primary" @click="fetchActivities">重试</el-button>
                    </template>
                </el-result>
            </div>

            <div v-else-if="loading" class="loading-grid">
                <el-skeleton v-for="n in 6" :key="n" animated class="skeleton-card">
                    <template #template>
                        <el-skeleton-item variant="image" style="width: 100%; height: 170px" />
                        <div style="padding: 14px 12px 18px">
                            <el-skeleton-item variant="h3" style="width: 65%; margin-bottom: 12px" />
                            <el-skeleton-item variant="text" style="width: 90%; margin-bottom: 8px" />
                            <el-skeleton-item variant="text" style="width: 55%" />
                        </div>
                    </template>
                </el-skeleton>
            </div>

            <div v-else-if="activities.length === 0" class="empty-state">
                <el-empty description="暂无活动，敬请期待" />
            </div>

            <div v-else-if="filteredActivities.length === 0" class="empty-state">
                <el-empty description="当前筛选条件下暂无活动">
                    <el-button type="primary" plain @click="resetFilters">重置筛选</el-button>
                </el-empty>
            </div>

            <div v-else>
                <div v-if="isClientFiltering" class="filter-tip">
                    当前展示的是本页筛选结果
                </div>

                <div class="activity-grid">
                    <el-card v-for="item in filteredActivities" :key="item.id" class="activity-card"
                        :body-style="{ padding: '0px' }" shadow="hover" @click="goToDetail(item)">
                        <div class="card-cover">
                            <el-image :src="item.coverImage || defaultCover" fit="cover">
                                <template #error>
                                    <div class="cover-fallback">封面加载失败</div>
                                </template>
                            </el-image>
                            <div v-if="!item.price || Number(item.price) <= 0" class="price-tag free">
                                免费
                            </div>
                        </div>
                        <div class="card-info">
                            <h3 class="title">{{ item.title }}</h3>
                            <p class="desc">{{ getDescriptionSnippet(item.description) }}</p>
                            <div class="meta">
                                <span class="enroll-count">
                                    <el-icon>
                                        <User />
                                    </el-icon>
                                    {{ item.enrollCount || 0 }} 人已参加
                                </span>
                            </div>
                            <div class="tags">
                                <el-tag v-for="tag in item.tags" :key="tag" size="small" effect="plain">{{ tag }}</el-tag>
                            </div>
                        </div>
                    </el-card>
                </div>
            </div>

            <div class="pagination" v-if="!isClientFiltering && total > pageSize">
                <el-pagination v-model:current-page="page" :page-size="pageSize" layout="prev, pager, next"
                    :total="total" @current-change="fetchActivities" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Search, User } from '@element-plus/icons-vue'
import api from '@/utils/api'

const router = useRouter()
const activities = ref([])
const loading = ref(false)
const total = ref(0)
const page = ref(1)
const pageSize = 12
const defaultCover = 'https://via.placeholder.com/400x225?text=Math+Course'
const keyword = ref('')
const selectedTag = ref('')
const sortType = ref('latest')
const errorMessage = ref('')

const normalizedActivities = computed(() => {
    return activities.value.map(item => ({
        ...item,
        tags: Array.isArray(item.tags) ? item.tags : []
    }))
})

const allTags = computed(() => {
    const set = new Set()
    normalizedActivities.value.forEach(item => item.tags.forEach(tag => set.add(tag)))
    return Array.from(set)
})

const filteredActivities = computed(() => {
    const keywordValue = keyword.value.toLowerCase()
    const list = normalizedActivities.value.filter(item => {
        const title = (item.title || '').toLowerCase()
        const matchKeyword = !keywordValue || title.includes(keywordValue)
        const matchTag = !selectedTag.value || item.tags.includes(selectedTag.value)
        return matchKeyword && matchTag
    })

    if (sortType.value === 'popular') {
        return [...list].sort((a, b) => (b.enrollCount || 0) - (a.enrollCount || 0))
    }
    if (sortType.value === 'priceAsc') {
        return [...list].sort((a, b) => (a.price || 0) - (b.price || 0))
    }
    return [...list].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
})

const totalEnrollCount = computed(() => {
    return normalizedActivities.value.reduce((sum, item) => sum + (item.enrollCount || 0), 0)
})

const freeActivityCount = computed(() => {
    return normalizedActivities.value.filter(item => !item.price || Number(item.price) <= 0).length
})

const isClientFiltering = computed(() => {
    return Boolean(keyword.value || selectedTag.value || sortType.value !== 'latest')
})

const fetchActivities = async () => {
    loading.value = true
    errorMessage.value = ''
    try {
        const res = await api.get('/api/activities', { params: { page: page.value, page_size: pageSize } })
        activities.value = res.data.activities || []
        total.value = res.data.total || 0
    } catch (e) {
        errorMessage.value = e.response?.data?.error || '请稍后重试'
        activities.value = []
        total.value = 0
    } finally {
        loading.value = false
    }
}

const goToDetail = (item) => {
    router.push(`/activities/${item.id}`)
}

const resetFilters = () => {
    keyword.value = ''
    selectedTag.value = ''
    sortType.value = 'latest'
}

const getDescriptionSnippet = (description) => {
    const plainText = String(description || '')
        .replace(/[#>*_`~\-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    if (!plainText) {
        return '围绕核心考点构建的专题训练活动，支持试卷学习、视频讲解与打卡复盘。'
    }
    return plainText.length > 58 ? `${plainText.slice(0, 58)}...` : plainText
}

onMounted(fetchActivities)
</script>

<style scoped>
.activity-list-page {
    min-height: 80vh;
    background: #ffffff;
    padding: 20px 0 44px;
}

.container {
    max-width: 1180px;
    margin: 0 auto;
    padding: 0 20px;
}

.content {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.header-card {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 12px;
    border-radius: 12px;
    border: 1px solid #dbe5f2;
    background: #ffffff;
    padding: 14px 16px;
    align-items: center;
}

.header-badge {
    display: inline-block;
    background: #e8f1ff;
    color: #225ea8;
    padding: 3px 10px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 8px;
    border: 1px solid #cfe2ff;
}

.header-title {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 4px;
    letter-spacing: -0.01em;
    color: #0f172a;
}

.header-subtitle {
    font-size: 13px;
    color: #64748b;
    margin: 0;
}

.header-stats {
    display: flex;
    gap: 8px;
}

.stat-item {
    min-width: 94px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 8px 10px;
    text-align: center;
}

.stat-value {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
    color: #0f172a;
}

.stat-label {
    margin-top: 2px;
    font-size: 11px;
    color: #64748b;
}

.toolbar-card {
    border-radius: 12px;
    border: 1px solid #dbe5f2;
    background: #ffffff;
    padding: 10px;
}

.toolbar {
    display: grid;
    grid-template-columns: 1fr 220px 180px;
    gap: 10px;
}

.filter-tip {
    font-size: 12px;
    color: #64748b;
    margin-bottom: 10px;
}

.error-state {
    background: #fff;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
}

.loading-grid,
.activity-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 14px;
}

.skeleton-card {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #dbe5f2;
    background: #fff;
}

.activity-card {
    border-radius: 12px;
    border: 1px solid #dbe5f2;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.activity-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08);
}

.card-cover {
    position: relative;
    aspect-ratio: 16/9;
    background: #f1f5f9;
}

.card-cover :deep(.el-image) {
    width: 100%;
    height: 100%;
}

.cover-fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b;
    font-size: 13px;
    background: #e2e8f0;
}

.price-tag {
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(15, 23, 42, 0.78);
    color: #fff;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
}

.price-tag.free {
    background: #16a34a;
}

.card-info {
    padding: 12px;
}

.title {
    font-size: 16px;
    line-height: 1.35;
    font-weight: 700;
    margin: 0 0 6px;
    color: #0f172a;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.desc {
    margin: 0 0 8px;
    font-size: 12px;
    line-height: 1.45;
    color: #64748b;
    min-height: 34px;
}

.meta {
    font-size: 12px;
    color: #64748b;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
}

.enroll-count {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.empty-state {
    border-radius: 12px;
    background: #fff;
    border: 1px solid #dbe5f2;
    padding: 20px;
}

.pagination {
    margin-top: 28px;
    display: flex;
    justify-content: center;
}

@media (max-width: 900px) {
    .header-card {
        grid-template-columns: 1fr;
    }

    .header-stats {
        justify-content: flex-start;
    }

    .toolbar {
        grid-template-columns: 1fr;
    }

    .header-title {
        font-size: 22px;
    }
}

@media (max-width: 640px) {
    .header-title {
        font-size: 20px;
    }

    .header-subtitle {
        font-size: 12px;
    }

    .header-stats {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .stat-item {
        min-width: 0;
        padding: 8px 4px;
    }
}
</style>
